import { useState } from 'react';
import { Calendar, Clock, Info, Link2, Lock, Milestone as MilestoneIcon, Paperclip } from 'lucide-react';
import { useT } from '../i18n';
import type { Task } from '../types';
import { taskDisplayCode } from '../utils/identifiers';

// A task's cross-project link, from the point of view of the project row
// showing this card. 'source' means this row is a linked (non-owning)
// project — the card is a read-only copy of a task that really lives, and is
// only editable, on its home project. 'alsoIn' means this row IS the home
// project, and the task is additionally linked into the given other projects.
export type TaskLinkInfo =
  | { kind: 'source'; code: string }
  | { kind: 'alsoIn'; codes: string[] };

interface TaskCardProps {
  task: Task;
  /** Someone else's task on a shared board (issue #29) — rendered secondary
   *  (muted) so the viewer's own tasks stand out, while staying visible. */
  dimmed?: boolean;
  linkInfo?: TaskLinkInfo;
  onTitleClick: () => void;
  onInfo?: (e: React.MouseEvent) => void;
  /** Task sits in a column named "Blocked" (issue #39) — shows/edits the reason inline. */
  isBlocked?: boolean;
  /** Omit (rather than passing a no-op) to render the reason as static text —
   *  used for a linked (non-home) row, where the task is read-only. */
  onBlockedReasonChange?: (reason: string) => void;
}

// Inline-editable "why is this blocked" line — click to edit, saves on blur.
function BlockedReasonField({ reason, onChange }: { reason: string | null; onChange: (reason: string) => void }) {
  const { t } = useT();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(reason || '');

  if (editing) {
    return (
      <textarea
        className="task-card-blocked-input"
        autoFocus
        value={value}
        placeholder={t('tasks.blockedReasonPlaceholder')}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => { setEditing(false); if (value.trim() !== (reason || '')) onChange(value.trim()); }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setValue(reason || ''); setEditing(false); }
        }}
      />
    );
  }

  return (
    <div
      className="task-card-blocked-reason"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
    >
      {reason || t('tasks.blockedReasonPlaceholder')}
    </div>
  );
}

export function TaskCard({ task, dimmed, linkInfo, onTitleClick, onInfo, isBlocked, onBlockedReasonChange }: TaskCardProps) {
  const { t } = useT();
  const isOverdue = task.due_date && new Date(task.due_date) < new Date();
  const isReadOnlyCopy = linkInfo?.kind === 'source';

  return (
    <div className={`task-card${dimmed ? ' task-card--dimmed' : ''}${isBlocked ? ' task-card--blocked' : ''}${isReadOnlyCopy ? ' task-card--locked' : ''}`}>
      {onInfo && (
        <button
          className="project-card-info"
          onClick={onInfo}
          title={t('tasks.viewDetails')}
        >
          <Info size={14} />
        </button>
      )}
      <div
        className="task-card-title"
        onClick={(e) => {
          e.stopPropagation();
          onTitleClick();
        }}
      >
        {task.title}
      </div>
      {isBlocked && (
        onBlockedReasonChange
          ? <BlockedReasonField reason={task.blocked_reason} onChange={onBlockedReasonChange} />
          : <div className="task-card-blocked-reason task-card-blocked-reason--static">{task.blocked_reason || t('tasks.blockedReasonPlaceholder')}</div>
      )}
      {linkInfo && (
        <div
          className="task-card-linked"
          title={linkInfo.kind === 'source' ? t('tasks.readOnlyNotice') : undefined}
        >
          <Link2 size={11} />
          {linkInfo.kind === 'source'
            ? `${t('tasks.primaryProjectPrefix')} ${linkInfo.code}`
            : t('tasks.presentInProjects', { projects: linkInfo.codes.join(', ') })}
        </div>
      )}
      {/* Linked milestone — only board tasks carry milestone_number (see GET /tasks/board). */}
      {task.milestone_number !== undefined && (() => {
        const label = task.milestone_number == null
          ? t('tasks.cardNoMilestone')
          : task.milestone_name?.trim()
            ? t('tasks.cardMilestone', { n: task.milestone_number, name: task.milestone_name.trim() })
            : t('tasks.cardMilestoneUntitled', { n: task.milestone_number });
        return (
          <div className={`task-card-milestone${task.milestone_number == null ? ' task-card-milestone--none' : ''}`} title={label}>
            <MilestoneIcon size={11} />
            <span>{label}</span>
          </div>
        );
      })()}
      <div className="task-card-meta">
        {(task.due_date || task.planned_at) && (
          <div className="task-card-dates">
            {task.due_date && (
              <span
                className={`task-card-due ${isOverdue ? 'task-card-due--overdue' : ''}`}
                title={t('tasks.dueDate')}
              >
                <Calendar size={12} />
                {new Date(task.due_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
              </span>
            )}
            {task.planned_at && (
              <span className="task-card-planned" title={t('tasks.plannedDate')}>
                <Clock size={12} />
                {new Date(task.planned_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                {' '}
                {new Date(task.planned_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        )}
        {task.assignee_name && (
          <span className="task-card-assignee">
            {task.assignee_name}
            {/* [Proposition: collaborateurs-tache] "+n" collaborators, named on hover. */}
            {!!task.collaborator_names?.length && (
              <span className="task-card-collab" title={t('collab.cardTitle', { names: task.collaborator_names.join(', ') })}>
                {' '}+{task.collaborator_names.length}
              </span>
            )}
          </span>
        )}
        {!task.assignee_name && !!task.collaborator_names?.length && (
          <span className="task-card-assignee" title={t('collab.cardTitle', { names: task.collaborator_names.join(', ') })}>
            {task.collaborator_names.join(', ')}
          </span>
        )}
        {/* Private task (set from the opened task): a closed padlock on the card. */}
        {task.is_private && (
          <span className="task-card-private-lock" title={t('tasks.privateTaskHelp')} aria-label={t('tasks.privateTask')}>
            <Lock size={12} />
          </span>
        )}
        {/* The task has documents (on it, its to-do items or its comments). */}
        {!!task.doc_count && (
          <span className="task-card-docs" title={t('docs.taskCount', { n: task.doc_count })}>
            <Paperclip size={12} />{task.doc_count}
          </span>
        )}
        {/* Stable short id (issue #27) — lets people reference a card
            unambiguously in meetings. */}
        <span className="card-id">{taskDisplayCode(task)}</span>
      </div>
    </div>
  );
}
